import type { StoredSession } from '@blooq/chat-store'
import type { TenantChrome } from '@blooq/flow'
import {
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonMenuToggle,
  IonPage,
  IonToolbar,
} from '@ionic/react'
import { arrowBackOutline, downloadOutline, menuOutline } from 'ionicons/icons'
import { useEffect, useRef, type ReactNode } from 'react'

import { useInstall } from '../lib/install'
import { tap } from '../lib/native'
import { monogram } from '../lib/theme'

import { HistoryMenu } from './HistoryMenu'
import { InstallSheet } from './InstallSheet'

/*
 * The chrome around the conversation.
 *
 * Header, scroller, dock, drawer — all of it Ionic's, and that is the whole
 * argument for the framework. Safe areas on a notched phone, a header that
 * knows it sits under a status bar, a drawer that opens on an edge swipe with
 * the platform's own easing, a back arrow that is a chevron on iOS and an arrow
 * on Android: none of it is hard, and all of it is the difference between an
 * app and a website in a WebView.
 *
 * The one thing that is ours is the DOCK, because no component library has an
 * opinion about "the place where the next answer is given". Everything the
 * customer can do lives there, in the same spot on every step.
 */

export interface ShellProps {
  tenant?: TenantChrome
  /** Showing a past conversation, which changes what the header offers. */
  reading?: boolean
  /** Set when the FLOW says this step has a back edge. Absent means no button. */
  onBack?: () => void

  sessions: StoredSession[]
  currentId?: string
  onOpenSession: (session: StoredSession) => void
  onNewConversation: () => void
  onClearHistory: () => void

  /** Changes when there is something new at the bottom of the transcript. */
  scrollKey?: string

  children: { transcript: ReactNode; dock: ReactNode }
}

const MENU_ID = 'blooq-history'
const CONTENT_ID = 'blooq-main'

export function Shell({
  tenant,
  reading,
  onBack,
  sessions,
  currentId,
  onOpenSession,
  onNewConversation,
  onClearHistory,
  scrollKey,
  children,
}: ShellProps) {
  const content = useRef<HTMLIonContentElement>(null)

  /*
   * One source of truth for the header button and the sheet.
   *
   * Splitting that state is how a button and a sheet come to disagree about
   * whether the app is already installed. It is off entirely in the native
   * shell, where the question is already answered.
   */
  const install = useInstall(true)

  /*
   * Keep the newest turn in sight.
   *
   * Deferred a frame because the dock changes height in the same commit — a
   * step offering four buttons leaves the scroller shorter than the one before
   * it, and scrolling against the pre-layout height lands short every time.
   */
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      void content.current?.scrollToBottom(scrollKey ? 250 : 0)
    })
    return () => cancelAnimationFrame(frame)
  }, [scrollKey])

  const subtitle = [tenant?.ownerName, tenant?.tagline].filter(Boolean).join(' · ')

  return (
    <>
      <HistoryMenu
        menuId={MENU_ID}
        contentId={CONTENT_ID}
        accent={tenant?.theme?.accent}
        sessions={sessions}
        currentId={currentId}
        onOpenSession={onOpenSession}
        onNewConversation={onNewConversation}
        onClearHistory={onClearHistory}
      />

      <IonPage id={CONTENT_ID}>
        {/* ion-no-border: Ionic's switch for the Material elevation shadow and
            the iOS hairline alike. The header is the same paper as the page. */}
        <IonHeader className="blooq-header ion-no-border">
          <IonToolbar className="blooq-toolbar">
            {/*
              One button on the left, and it is the one that matters most
              right now. On the front door and at the end of a branch that is
              the drawer — past conversations, and the way to start another.
              On a step with something behind it, it is Back: the drawer still
              opens on an edge swipe, but a customer three answers into a
              booking needs the arrow more than the list.
            */}
            <IonButtons slot="start">
              {onBack ? (
                <IonButton
                  className="blooq-iconbtn"
                  onClick={() => {
                    tap()
                    onBack()
                  }}
                  aria-label={reading ? 'Back to the conversation' : 'Back'}
                >
                  <IonIcon slot="icon-only" icon={arrowBackOutline} />
                </IonButton>
              ) : (
                <IonMenuToggle menu={MENU_ID} autoHide={false}>
                  <IonButton
                    className="blooq-iconbtn"
                    onClick={() => tap()}
                    aria-label="Menu and past conversations"
                  >
                    <IonIcon slot="icon-only" icon={menuOutline} />
                  </IonButton>
                </IonMenuToggle>
              )}
            </IonButtons>

            <div className="blooq-identity">
              {tenant?.logoUrl ? (
                <img className="blooq-avatar" src={tenant.logoUrl} alt="" />
              ) : (
                <span className="blooq-avatar" aria-hidden="true">
                  {monogram(tenant?.businessName ?? '')}
                </span>
              )}
              <span className="blooq-identity__text">
                <span className="blooq-identity__name">
                  {tenant?.businessName ?? ' '}
                </span>
                {subtitle ? <span className="blooq-identity__sub">{subtitle}</span> : null}
              </span>
            </div>

            {/* Only where the app can be installed and is not yet. Nothing
                else lives on the right, so the name has the room. */}
            {install.mode !== 'hidden' ? (
              <IonButtons slot="end">
                <IonButton
                  className="blooq-iconbtn"
                  onClick={install.openSheet}
                  aria-label="Add to home screen"
                >
                  <IonIcon slot="icon-only" icon={downloadOutline} />
                </IonButton>
              </IonButtons>
            ) : null}
          </IonToolbar>
        </IonHeader>

        <IonContent className="blooq-content" ref={content} scrollEvents={false}>
          <div className="blooq-transcript">{children.transcript}</div>
        </IonContent>

        {children.dock || tenant?.showPoweredBy ? (
          <IonFooter className="blooq-dock ion-no-border">
            {children.dock ? <div className="blooq-dock__in">{children.dock}</div> : null}
            {/* A tenant on a plan that carries the credit shows it; nobody
                else does. The server decides, this only prints. */}
            {tenant?.showPoweredBy ? (
              <div className="blooq-powered">Powered by EastLaunch</div>
            ) : null}
          </IonFooter>
        ) : null}
      </IonPage>

      <InstallSheet state={install} businessName={tenant?.businessName} />
    </>
  )
}
